import React from 'react'
import { useConnectorFormProps, useConnectorRequirementDescription, useSelectOptions } from './ConnectorStep.hooks'
import Autocomplete from '@/components/ui/Autocomplete'
import Typography from '@/components/ui/Typography'
import { ConnectorForm } from '@/features/connectors'
import { useField, useFormikContext } from 'formik'
import Button from '@/components/ui/Buttons/Button'
import { ConnectorTemplateRequirement, useTemplateStepsContext } from '@/features/templates'
import FormLabel from '@/components/ui/FormLabel'
import { capitalCase } from 'change-case'

export type ConnectorStepProps = {
  name: string
  requirement: ConnectorTemplateRequirement
}

export const ConnectorStep = ({ name, requirement }: ConnectorStepProps) => {
  const { setFieldValue } = useFormikContext()
  const [, { value }] = useField(name)
  const system = requirement.system ?? 'GENERIC'
  const description = useConnectorRequirementDescription(requirement)
  const options = useSelectOptions(system)
  const [isNoInitialOptions] = React.useState(options.length === 0)
  const [isExpanded, setIsExpanded] = React.useState(isNoInitialOptions)
  const formProps = useConnectorFormProps({
    name,
    system,
  })
  const { currentStep, setCurrentStep } = useTemplateStepsContext()

  return (
    <div style={{ width: 480 }}>
      <Typography variant={'xxl'}>Connect to {capitalCase(system)}</Typography>
      <Typography>{description}</Typography>
      <br />
      {isExpanded ? (
        <div className={'d-grid gap-3'}>
          <div className="knots-form">
            <ConnectorForm {...formProps} />
          </div>
          {!isNoInitialOptions && (
            <Button onClick={() => setIsExpanded(false)} color={'neutral'} variant={'outlined'}>
              Select existing
            </Button>
          )}
        </div>
      ) : (
        <div className={'d-grid gap-3'}>
          <div>
            <FormLabel>Select existing connector</FormLabel>
            <Autocomplete
              name={requirement.key}
              value={value}
              onChange={setFieldValue}
              options={options}
              selectOnly
              disableClearable
            />
          </div>

          <Button onClick={() => setIsExpanded(true)} color={'neutral'} variant={'outlined'}>
            Create new connector
          </Button>

          <div className={'d-flex gap-2 justify-content-between'}>
            {currentStep > 0 ? (
              <Button onClick={() => setCurrentStep((currentStep) => currentStep - 1)} color={'neutral'}>
                Back
              </Button>
            ) : (
              <div />
            )}
            <Button onClick={() => setCurrentStep((currentStep) => currentStep + 1)} disabled={!value}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
